import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { useParams } from 'react-router-dom'
import { DocumentTypes, FORMATS } from 'builder/modules/constants'

import { ResumeEditorStepByStep } from 'builder/components/DocumentEditor'
import PreviewPanel, { MODES as PREVIEW_PANEL_MODES } from 'builder/components/PreviewPanel'

import { actions, PrefillStatuses, selectors, STEP_NAMES } from 'builder/modules/resumeEditor'
import { actions as renderingActions } from 'builder/modules/rendering'
import { actions as coRegActions } from 'builder/modules/coRegistration'
import { actions as generalEditorActions } from 'builder/modules/generalEditor'
import { navigate } from 'builder/modules/navigate'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { Container, LeftSide, FloatingDownloadPdfButton } from './styles'

export const ResumeEditorStepByStepView = () => {
  const isDownloading = useTypedSelector(state => state.rendering.isDocumentDownloading)
  const editorState = useTypedSelector(state => state.generalEditor)
  const resumeState = useTypedSelector(state => state.resumeEditor)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const { status: prefillingStatus } = useTypedSelector(selectors.prefill)
  const isPrefilling = prefillingStatus === PrefillStatuses.scheduled
  const params = useParams()
  const mediaQueries = useMediaQueries()
  const dispatch = useDispatch()
  const resumeId = parseInt(params.id || '')
  const { resume, isSyncing, currentWizardStepId } = resumeState

  useEffect(() => {
    navigate(`/resumes/${resumeId}/edit`)
    dispatch(actions.fetchResumeRequest({ id: resumeId }))
    dispatch(generalEditorActions.fetchEditorPayloadRequest())
    dispatch(coRegActions.initialize())
    // track the page opening
    trackInternalEvent('visit_resume_editor', { resume_id: resumeId, editor: 'wizard' })
  })

  useEffect(() => {
    if (resumeState.error) navigate('/resumes')
  }, [resumeState.error])

  const handleDownloadPdf = useCallback(() => {
    dispatch(
      renderingActions.download({ id: resumeId, type: DocumentTypes.resume, format: FORMATS.pdf }),
    )

    // Track in Google Analytics
    trackMarketingEvent('Click Download', 'Download Resume', { eventLabel: FORMATS.pdf })
    // Track in ClickHouse
    trackInternalEvent('click_download_resume_button', { format: FORMATS.pdf, source: 'wizard' })
  }, [dispatch, resumeId])

  return (
    <Container>
      <LeftSide>
        <ResumeEditorStepByStep editorState={editorState} resumeState={resumeState} />
      </LeftSide>

      {/*
        Unmount PreviewPanel when DocumentPreviewModal is visible. Reasons:
        - prevent render if block is invisible - reduce the load of user's device
        - prevent parallel tasks - pdfjs worker can't do more than one in parallel
        */}
      {mediaQueries.isDesktop && resume && (
        <PreviewPanel
          type={DocumentTypes.resume}
          documentId={resumeId}
          isSyncing={isSyncing}
          isDownloading={isDownloading}
          currentDocument={resume}
          mode={PREVIEW_PANEL_MODES.wizard}
          isOnline={isOnline}
          isPrefilling={isPrefilling}
        />
      )}

      <FloatingDownloadPdfButton
        onClick={handleDownloadPdf}
        $isShow={currentWizardStepId !== STEP_NAMES.details}
      >
        Download PDF
      </FloatingDownloadPdfButton>
    </Container>
  )
}
